import { buildApiUrl } from '../../services/apiConfig';

class GuardrailsService {
  constructor() {
    this.API_HOST = '18.208.58.92';
    this.baseUrl = buildApiUrl('GUARD');
  }

  async getAllConfigurations() {
    try {
      const response = await fetch(`${this.baseUrl}/guardrails/config`);
      if (!response.ok) return [];
      return response.json();
    } catch (error) {
      console.warn('Guardrails service unavailable:', error.message);
      return [];
    }
  }

  async getConfiguration(configName) {
    const response = await fetch(`${this.baseUrl}/guardrails/config/${configName}`);
    if (!response.ok) throw new Error('Failed to fetch configuration');
    return response.json();
  }

  async createConfiguration(configName) {
    const response = await fetch(`${this.baseUrl}/guardrails/config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        use_case: configName,
        template: 'default'
      })
    });
    if (!response.ok) throw new Error('Failed to create configuration');
    return response.json();
  }

  async updateConfiguration(configName, configData) {
    const response = await fetch(`${this.baseUrl}/guardrails/config/${configName}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(configData)
    });
    if (!response.ok) throw new Error('Failed to save configuration');
    return response.json();
  }

  async deleteConfiguration(configName) {
    const response = await fetch(`${this.baseUrl}/guardrails/config/${configName}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete configuration');
    return response.json();
  }

  async getPromptTemplate(configName, templateType) {
    try {
      const response = await fetch(`${this.baseUrl}/guardrails/config/${configName}/prompt/${templateType}`);
      if (!response.ok) return { content: '' };
      return response.json();
    } catch (error) {
      console.warn('Guardrails service unavailable:', error.message);
      return { content: '' };
    }
  }

  async updatePromptTemplate(configName, templateType, content) {
    try {
      const response = await fetch(`${this.baseUrl}/guardrails/config/${configName}/prompt/${templateType}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.warn(`Guardrails API error for ${templateType}:`, errorData.error || response.statusText);
        // Return success to prevent UI errors - this is a non-critical service
        return { success: false, error: errorData.error };
      }
      return response.json();
    } catch (error) {
      console.warn(`Failed to update ${templateType} template:`, error.message);
      // Return success to prevent UI errors
      return { success: false, error: error.message };
    }
  }

  async testGuardrails(configName, testType, testText) {
    // Declare requestBody outside try block so it's accessible in catch
    let requestBody = {
      use_case: configName,
      check_type: testType
    };
    
    try {
      // Always provide both user_input and bot_response to avoid backend errors
      if (testType === 'input') {
        requestBody.user_input = testText;
        requestBody.bot_response = '';  // Empty string instead of null
      } else {
        requestBody.user_input = 'User query';  // Provide a default context
        requestBody.bot_response = testText;
      }
      
      // Create an AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      try {
        const response = await fetch(`${this.baseUrl}/guardrails`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          const errorText = await response.text();
          return { 
            success: false, 
            error: `API returned ${response.status}: ${errorText}`,
            request: requestBody
          };
        }
        
        const contentType = response.headers.get('content-type');
        let result;
        
        if (contentType && contentType.includes('application/json')) {
          result = await response.json();
        } else {
          result = await response.text();
        }
        
        return {
          success: true,
          test_type: testType,
          config_name: configName,
          test_text: testText,
          api_response: result,
          is_json: typeof result === 'object',
          request_sent: requestBody,
          timestamp: new Date().toISOString()
        };
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          return {
            success: false,
            error: 'Request timed out after 30 seconds',
            details: 'The guardrails API is taking too long to respond. This may indicate a backend processing issue.',
            request: requestBody
          };
        }
        throw fetchError;
      }
    } catch (error) {
      console.error('Failed to test guardrails:', error);
      return { 
        success: false, 
        error: error.message,
        details: 'Failed to connect to guardrails API. Please check if the service is running.',
        request: requestBody
      };
    }
  }

  checkSensitivePatterns(text) {
    const patterns = {
      potential_pii: /\b\d{3}-\d{2}-\d{4}\b|\b\d{16}\b/.test(text),
      potential_prompt_injection: /ignore (previous|above|all) instructions?/i.test(text),
      potential_jailbreak: /\b(DAN|developer mode|act as|pretend you are)\b/i.test(text)
    };
    return patterns;
  }
}

// Export a singleton instance
export default new GuardrailsService();